import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import CrmLayout from "../../components/CrmLayout";
import api from "../../lib/api";
import { money } from "../../lib/format";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { CalendarClock, Users, CalendarDays, DollarSign, Plus, Sparkles, TrendingUp, TrendingDown, Camera, X } from "lucide-react";
import { useAuth } from "../../context/AuthContext";

function greetingFor(name) {
  const h = new Date().getHours();
  const part = h < 12 ? "Good morning" : h < 17 ? "Good afternoon" : "Good evening";
  const first = (name || "").trim().split(" ")[0];
  return first ? `${part}, ${first}` : part;
}

const STATUS_STYLE = {
  scheduled: "bg-secondary text-secondary-foreground", confirmed: "bg-primary/10 text-primary",
  completed: "bg-[hsl(var(--success))]/15 text-[hsl(var(--success))]", cancelled: "bg-destructive/10 text-destructive",
  no_show: "bg-muted text-muted-foreground", pending_deposit: "bg-accent/20 text-accent-foreground",
};

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const navigate = useNavigate();
  const { user } = useAuth();
  const promptKey = user?.id ? `avatar_prompt_dismissed:${user.id}` : "avatar_prompt_dismissed";
  const [avatarPromptDismissed, setAvatarPromptDismissed] = useState(false);
  useEffect(() => { setAvatarPromptDismissed(localStorage.getItem(promptKey) === "1"); }, [promptKey]);
  useEffect(() => { api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => {}); }, []);
  const dismissAvatarPrompt = () => { localStorage.setItem(promptKey, "1"); setAvatarPromptDismissed(true); };
  const missingAvatars = stats?.stylists_missing_avatar || 0;
  const showAvatarPrompt = user?.role === "owner" && missingAvatars > 0 && !avatarPromptDismissed;

  const todayRevenue = stats ? (stats.today_appointments || []).reduce((sum, a) => sum + (Number(a.price) || 0), 0) : 0;
  const headerStats = stats ? [
    { label: "Today's bookings", value: stats.today_count },
    { label: "Today's revenue", value: money(todayRevenue) },
  ] : [];

  const cards = stats ? [
    { label: "Today", value: stats.today_count, icon: CalendarClock },
    { label: "Upcoming", value: stats.upcoming_count, icon: CalendarDays },
    { label: "Clients", value: stats.total_clients, icon: Users },
    { label: "Revenue", value: money(stats.revenue), icon: DollarSign },
  ] : [];

  return (
    <CrmLayout title="Dashboard" greeting={greetingFor(user?.name)} stats={headerStats} actions={<Button className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-md" data-testid="dash-new-appt" onClick={() => navigate("/appointments")}><Plus size={16} className="mr-1" /> New appointment</Button>}>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {cards.map((c) => {
          const Icon = c.icon;
          return (
            <div key={c.label} className="bg-card border border-border rounded-lg p-5" data-testid={`stat-${c.label}`}>
              <div className="flex items-center justify-between mb-2">
                <span className="label-eyebrow text-muted-foreground">{c.label}</span>
                <Icon size={16} className="text-primary" aria-hidden="true" />
              </div>
              <div className="font-display text-3xl font-bold">{c.value}</div>
            </div>
          );
        })}
      </div>

      {showAvatarPrompt && (
        <div className="rounded-lg p-5 mb-6 bg-primary/5 border border-primary/30 flex items-center justify-between flex-wrap gap-3" data-testid="avatar-prompt-banner">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-primary/15 text-primary flex items-center justify-center shrink-0"><Camera size={18} /></div>
            <div>
              <div className="font-semibold">Add team photos to lift bookings</div>
              <p className="text-sm text-muted-foreground">{missingAvatars} {missingAvatars === 1 ? "team member is" : "team members are"} missing a photo. Clients book faster when they can put a face to the name.</p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-md" data-testid="avatar-prompt-cta" onClick={() => navigate("/team")}>Add team photos</Button>
            <Button variant="ghost" size="sm" className="text-muted-foreground" data-testid="avatar-prompt-dismiss" onClick={dismissAvatarPrompt}><X size={16} /></Button>
          </div>
        </div>
      )}

      {stats && (stats.waitlist_revenue > 0 || stats.waitlist_claimed > 0) && (() => {
        const tw = stats.waitlist_revenue_this_week || 0;
        const lw = stats.waitlist_revenue_last_week || 0;
        let trend = null;
        if (lw > 0) trend = Math.round(((tw - lw) / lw) * 100);
        else if (tw > 0) trend = 100;
        const up = trend != null && trend >= 0;
        const TrendIcon = up ? TrendingUp : TrendingDown;
        return (
          <div className="rounded-lg p-5 mb-6 bg-accent/10 border border-accent/30 flex items-center justify-between flex-wrap gap-3" data-testid="dash-waitlist-recovery">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-accent/20 text-accent flex items-center justify-center"><Sparkles size={18} /></div>
              <div>
                <div className="label-eyebrow text-muted-foreground">Recovered by waitlist</div>
                <div className="font-display text-3xl font-bold text-accent">{money(stats.waitlist_revenue)}</div>
              </div>
            </div>
            <div className="flex flex-col items-end gap-1.5 text-right">
              {trend != null && (
                <span data-testid="recovery-trend" className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-full ${up ? "bg-[hsl(var(--success))]/15 text-[hsl(var(--success))]" : "bg-destructive/10 text-destructive"}`}>
                  <TrendIcon size={13} /> {up ? "+" : ""}{trend}% vs last week
                </span>
              )}
              <p className="text-sm text-muted-foreground max-w-xs">{money(tw)} this week · from <span className="font-semibold text-foreground">{stats.waitlist_claimed}</span> {stats.waitlist_claimed === 1 ? "spot" : "spots"} filled off your waitlist.</p>
            </div>
          </div>
        );
      })()}
      <div className="bg-card border border-border rounded-lg">
        <div className="px-5 py-4 border-b border-border"><h2 className="font-semibold">Today's schedule</h2></div>
        {stats && stats.today_appointments.length === 0 && <div className="p-10 text-center text-muted-foreground text-sm">No appointments today.</div>}
        <div className="divide-y divide-border">
          {stats?.today_appointments.map((a) => (
            <div key={a.id} className="px-5 py-3 flex items-center justify-between" data-testid={`today-${a.id}`}>
              <div className="flex items-center gap-4">
                <span className="font-mono text-sm w-14 text-primary">{a.start_time}</span>
                <div>
                  <div className="text-sm font-medium">{a.client_name}</div>
                  <div className="text-xs text-muted-foreground">{a.service_name} · {a.stylist_name}</div>
                </div>
              </div>
              <Badge className={STATUS_STYLE[a.status]}>{a.status.replace("_", " ")}</Badge>
            </div>
          ))}
        </div>
      </div>
    </CrmLayout>
  );
}
